import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  MessageSquare, Send, Phone, User, Clock, 
  CheckCircle, XCircle, AlertCircle, RefreshCw,
  ChevronRight, Search, Filter
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dashboard de Conversaciones de WhatsApp
 * Para Admin/SubAdmin - Ver y gestionar conversaciones de upsell
 */
export const ConversationsDashboard = ({ adminId, subAdminId = null }) => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, consultation_scheduled, opted_out
  const [searchTerm, setSearchTerm] = useState('');
  
  const messagesEndRef = useRef(null);

  const content = {
    es: {
      title: "Conversaciones",
      subtitle: "Gestiona las respuestas de WhatsApp de negocios",
      noConversations: "No hay conversaciones",
      noConversationsDesc: "Las conversaciones aparecerán aquí cuando los negocios respondan",
      selectConversation: "Selecciona una conversación",
      search: "Buscar negocio...",
      filters: {
        all: "Todas",
        active: "Activas",
        consultation_scheduled: "Consulta Agendada",
        opted_out: "Baja"
      },
      status: {
        active: "Activa",
        consultation_scheduled: "Consulta Agendada",
        opted_out: "Baja solicitada",
        pending_contact: "Pendiente contacto"
      },
      intent: {
        book_consultation: "Quiere agendar",
        interested: "Interesado",
        opt_out: "Solicita baja",
        demo_request: "Pide demo",
        question: "Pregunta",
        other: "Otro"
      },
      sendPlaceholder: "Escribe un mensaje...",
      send: "Enviar",
      lastMessage: "Último mensaje",
      potential: "Potencial",
      contacts: "contactos",
      refresh: "Actualizar",
      callNow: "Llamar ahora",
      viewProfile: "Ver perfil"
    },
    en: {
      title: "Conversations",
      subtitle: "Manage WhatsApp responses from businesses",
      noConversations: "No conversations",
      noConversationsDesc: "Conversations will appear here when businesses respond",
      selectConversation: "Select a conversation",
      search: "Search business...",
      filters: {
        all: "All",
        active: "Active",
        consultation_scheduled: "Consultation Scheduled",
        opted_out: "Opted Out"
      },
      status: {
        active: "Active",
        consultation_scheduled: "Consultation Scheduled",
        opted_out: "Opted out",
        pending_contact: "Pending contact"
      },
      intent: {
        book_consultation: "Wants to book",
        interested: "Interested",
        opt_out: "Wants to opt out",
        demo_request: "Requests demo",
        question: "Question",
        other: "Other"
      },
      sendPlaceholder: "Write a message...",
      send: "Send",
      lastMessage: "Last message",
      potential: "Potential",
      contacts: "contacts",
      refresh: "Refresh",
      callNow: "Call now",
      viewProfile: "View profile"
    }
  };

  const c = isSpanish ? content.es : content.en;

  useEffect(() => {
    fetchConversations();
    // Polling cada 30 segundos
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [adminId, filter]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.business_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const headers = { 'X-Admin-Id': adminId };
      if (subAdminId) headers['X-SubAdmin-Id'] = subAdminId;
      
      const { data } = await axios.get(`${API}/upsell/conversations`, {
        headers,
        params: { status: filter !== 'all' ? filter : undefined }
      });
      
      setConversations(data.conversations || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (businessId) => {
    try {
      const headers = { 'X-Admin-Id': adminId };
      
      const { data } = await axios.get(`${API}/upsell/conversations/${businessId}/messages`, {
        headers
      });
      
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setMessages(selectedConversation?.messages || []);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      setSending(true);
      
      await axios.post(
        `${API}/upsell/conversations/${selectedConversation.business_id}/send`,
        { message: newMessage },
        { headers: { 'X-Admin-Id': adminId } }
      );
      
      // Añadir mensaje localmente
      setMessages(prev => [...prev, {
        direction: 'outbound',
        content: newMessage,
        timestamp: new Date().toISOString(),
        sent_by: 'admin'
      }]);
      
      setNewMessage('');
      toast.success(isSpanish ? 'Mensaje enviado' : 'Message sent');
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sending message');
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    return conv.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           conv.phone?.includes(searchTerm);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'consultation_scheduled': return 'bg-blue-500';
      case 'opted_out': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getIntentIcon = (intent) => {
    switch (intent) {
      case 'book_consultation': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'interested': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'opt_out': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <MessageSquare className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return isSpanish ? 'ahora' : 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden min-h-[500px] flex" data-testid="conversations-dashboard">
      {/* Sidebar - Lista de conversaciones */}
      <div className="w-80 border-r border-gray-700 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">{c.title}</h3>
            <button 
              onClick={fetchConversations}
              className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder={c.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>
          
          {/* Filters */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {Object.entries(c.filters).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1 text-xs rounded-full whitespace-nowrap ${
                  filter === key 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : filteredConversations.length > 0 ? (
            filteredConversations.map((conv) => (
              <div
                key={conv.business_id}
                onClick={() => setSelectedConversation(conv)}
                className={`p-4 border-b border-gray-800 cursor-pointer transition-colors ${
                  selectedConversation?.business_id === conv.business_id 
                    ? 'bg-gray-800' 
                    : 'hover:bg-gray-800/50'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(conv.status)}`} />
                    <span className="font-medium text-white text-sm truncate max-w-[150px]">
                      {conv.business_name || 'Sin nombre'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatTime(conv.last_message_at)}
                  </span>
                </div>
                
                <p className="text-xs text-gray-400 truncate mb-2">
                  {conv.messages?.[conv.messages.length - 1]?.content || 'Sin mensajes'}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {getIntentIcon(conv.messages?.[conv.messages.length - 1]?.intent)}
                    <span className="text-xs text-gray-500">
                      {c.intent[conv.messages?.[conv.messages.length - 1]?.intent] || ''}
                    </span>
                  </div>
                  {conv.potential_revenue && (
                    <span className="text-xs text-emerald-400 font-medium">
                      €{conv.potential_revenue?.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{c.noConversations}</p>
              <p className="text-xs mt-1">{c.noConversationsDesc}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Main - Conversation Detail */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Conversation Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">{selectedConversation.business_name}</h4>
                  <p className="text-xs text-gray-400">{selectedConversation.phone}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  selectedConversation.status === 'consultation_scheduled' 
                    ? 'bg-blue-900/50 text-blue-400' 
                    : selectedConversation.status === 'active'
                    ? 'bg-green-900/50 text-green-400'
                    : 'bg-gray-700 text-gray-400'
                }`}>
                  {c.status[selectedConversation.status] || selectedConversation.status}
                </span>
                
                {selectedConversation.phone && (
                  <a 
                    href={`tel:${selectedConversation.phone}`}
                    className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                    title={c.callNow}
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                    msg.direction === 'outbound'
                      ? 'bg-emerald-600 text-white rounded-br-sm'
                      : 'bg-gray-700 text-white rounded-bl-sm'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <div className="flex items-center justify-end gap-2 mt-1">
                      <span className="text-xs opacity-70">
                        {formatTime(msg.timestamp)}
                      </span>
                      {msg.auto && (
                        <span className="text-xs opacity-50">Auto</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Message Input */}
            <div className="p-4 border-t border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder={c.sendPlaceholder}
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  disabled={sending}
                />
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{c.selectConversation}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsDashboard;
